import styled from "styled-components";
import { useInView } from 'react-intersection-observer';
import anime  from 'animejs';
import { mq } from "../util/media";

import Button from "../Button";
import LearnMoreButton from "../LearnMoreButton";
import GradientText from "../GradientText";
import SectionWrapper from "../SectionWrapper";
import SectionTitle from "../SectionTitle";


import { ReactComponent as DigitalJournal } from "../../images/digital_journal.svg";
import { ReactComponent as BlockoNews } from "../../images/blocko_news.svg";
import { ReactComponent as AsiaToken } from "../../images/asia_token.svg";
import { ReactComponent as Scoopnix } from "../../images/scoopnix.svg";
import { ReactComponent as BullshitNet } from "../../images/bullish_net.svg";
import { ReactComponent as WorldCryptoTimes } from "../../images/world_crypto_times.svg";
import { ReactComponent as NearInsider } from "../../images/near_insider.svg";
import { ReactComponent as AwesomeNear } from "../../images/awesome_near.svg";

import { useEffect } from "react";
import { useWindowSize } from "../util/viewsize";

const MainText = styled.p`
  font-size: 20px;
  letter-spacing: 0.5px;
  line-height: 1.2;
  margin: 0;
  max-width: 660px;
  margin-bottom: 24px;


  img {
    max-height: 2.75rem;
    margin-left: 2rem;
  }

  ${mq.lt.md} {
    font-size: 15px;
  }

  ${mq.lt.sm} {
    text-align: center;
    font-size: 11px;
    max-width: 265px;
    margin-left: auto;
    margin-right: auto;
  }
`

const ButtonContainer = styled.div`

  display: flex;
  justify-content: center;
  gap: 1rem;

  ${mq.gt.xs} {
    gap: 2rem;
    justify-content: flex-start;
  }
`

const ImageContainer = styled.div`
  display: flex; 
  flex-direction: column;
  height: fit-content;
  justify-content: space-between;
  row-gap: 1.2rem;
  width: 225px;
  margin: 0 auto;

  & svg {
    width: auto;
    height: 100%;
    max-height: 21px;
    object-fit: contain;
    color: #8A888D;
    shape-rendering: geometricPrecision;
  }

  //specific fixes for certain logos
  & .digital-journal {
    max-width: 65%;
  }

  & .scoopnix {
    max-width: 45%;
  }

  & > div {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }



  ${mq.gt.xs} {
    width: 260px;
    row-gap: 24px;
    margin: unset;
    margin-left: auto;

    svg {
      max-height: 26px;
    }
  }

  ${mq.gt.sm} {
    width: 455px;
    row-gap: 60px;
    
    svg {
      max-height: 36px;
    }

    //specific fixes for certain logos
    & .near-insider {
      max-height: 46px; 
    }

    & .digital-journal {
      max-width: 55%;
    }

    & .awesome-near {
      max-width: 35%;
    }
  }

  transition: opacity 0.5s ease-in-out;
  &.hidden {
    opacity: 0;
  }

`

const InfoWrapper = styled.div`
  margin: 5rem 0;
  ${mq.lt.md} {
    margin: 45px 0;
  }
  transition: 0.7s ease-in-out;

  &.hidden {
    opacity: 0;
    transform: translateX(-10%);
  }
`

const Inner = styled.div`
  display: flex;
  flex-direction: column;


  ${mq.gt.xs} {
    
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 40px;
    width: 100%;

    align-items: center;
  }

  ${mq.gt.sm} {
    grid-template-columns: 2fr 1fr;
  }
`

const SectionTitleStyled = styled(SectionTitle)`
  max-width: 16ch;
  margin-left: auto;
  margin-right: auto;
`

function WebApp() {

  const [textRef, textInView] = useInView({
    triggerOnce: true,
    threshold: 0.6
  });

  const [imageRef, imageInView] = useInView({
    triggerOnce: true,
    threshold: 0.6
  });

  var tl = anime.timeline({
    autoplay: false
  });

  tl.add({
    targets: ".container--media-images svg",
    delay: anime.stagger(100),
    opacity: [0, 1],
    easing: 'easeInQuad',
  });

  useEffect (() => {
    if (imageInView) {
      tl.play();
    }
  }, [tl, imageInView]);

  const [ width ] = useWindowSize();

  return(
    <SectionWrapper smaller>


      <Inner>
        <InfoWrapper ref={textRef} className={textInView ? "" : "hidden" }>
          <SectionTitleStyled>Near Treasury featured in <br/><GradientText to="right" color1="#EF9779" color2="#D875C8">the Media</GradientText></SectionTitleStyled>
          <MainText>
            Near Treasury has been featured in well-known online publications such as: Digital Journal, World Crypto Times, and many more! <br />We are in talks with other media publications which will bring further attention to the project.
          </MainText>
          <ButtonContainer>
            <LearnMoreButton>Learn More</LearnMoreButton>
            <Button link="/webapp" type="webapp">WebApp</Button>
          </ButtonContainer>
        </InfoWrapper>
        <ImageContainer ref={imageRef} className="container--media-images">
          {width < 1224 ? (
            <>
              <div>
                <DigitalJournal className='digital-journal' />
                <NearInsider className='near-insider' />
              </div>
              <div>
                <AwesomeNear className='awesome-near' />
                <WorldCryptoTimes className='world-crypto-times' />
              </div>
              <div>
                <AsiaToken className='asia-token' />
                <BullshitNet className='bullish-net' />
              </div>
              <div>
                <Scoopnix className='scoopnix' />
                <BlockoNews className="blocko-news" />
              </div>
            </>
          ) : (
            <>
              <div>
                <DigitalJournal className='digital-journal' />
                <AwesomeNear className='awesome-near' />
              </div>
              <div>
                <AsiaToken className='asia-token' />
                <NearInsider className='near-insider' />
                <Scoopnix className='scoopnix' />
              </div>
              <div>
                <BullshitNet className='bullish-net' />
                <BlockoNews className="blocko-news" />
                <WorldCryptoTimes className='world-crypto-times' />
              </div>
            </>
          )}
        </ImageContainer>
      
      </Inner>


    </SectionWrapper>

  )
}

export default WebApp;